import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { VbidParams } from './VbidPage.metadata';
import { from, Observable } from 'rxjs';
import { filter, map, toArray } from 'rxjs/operators';

export interface UnauthenticatedVbidFormProps {
    memberData: VbidParams;
    onFormSubmitted: () => void;
}

export interface SubmitResponse {
    success: boolean;
    message: string;
    caseNo: string;
}

export interface VbidForm {
    memberId: string;
    answer1: string | '';
    answer2: LivingSituation[];
    answer3: string[];
    answer4: Answer4;
    answer5: TransportationDifficulty[];
    wontAnswer: WontAnswerField[];
    dirtyFields: DirtyField[];
}

export const DefaultVbidForm = (memberId: string): VbidForm => ({
    memberId: memberId,
    answer1: '',
    answer2: [],
    answer3: [],
    answer4: { selected: [], other: '' },
    answer5: [],
    wontAnswer: [],
    dirtyFields: [],
});

export type VbidFormProcessor = (f: VbidForm) => VbidForm;

type Key = keyof VbidForm;
type Value<k extends Key> = VbidForm[k];
type ChangeSet = [Key, Value<Key>];
const ApplyChange = (changeSet: ChangeSet): VbidFormProcessor =>
    (f) => ({
        ...cloneDeep(f),
        [changeSet[0]]: changeSet[1],
    });

export const GetPipeline =
    (...changeSet: ChangeSet[]) =>
        (form: VbidForm): Observable<VbidForm> => {
            let pipeline2 = from([form]);
            for (const c of changeSet) {
                pipeline2 = pipeline2.pipe(map(ApplyChange(c)));
            }
            return pipeline2;
        };

type ExtractType<T, U extends T> = T extends U ? T : never;
export type HtmlInputHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;
export type AnswerField = ExtractType<keyof VbidForm, 'answer1' | 'answer2' | 'answer3' | 'answer4' | 'answer5'>;
export type ResetValue = ExtractType<keyof VbidForm, 'answer1' | 'answer2' | 'answer3' | 'answer4' | 'answer5'>;

const DirtyFields = ['dirty1', 'dirty2', 'dirty3', 'dirty4', 'dirty5'] as const satisfies readonly string[];
export type DirtyField = typeof DirtyFields[number];

const WontAnswerFields = [
    'wontAnswer1',
    'wontAnswer2',
    'wontAnswer3',
    'wontAnswer4',
    'wontAnswer5',
] as const satisfies readonly string[];
export type WontAnswerField = typeof WontAnswerFields[number];

export const LivingSituations = [
    'I have housing',
    'I do not have housing (staying with others, in a hotel, in a shelter, living outside on the street, on a beach, in a car, or in a park)',
] as const satisfies readonly string[];
export type LivingSituation = typeof LivingSituations[number];

export const FamilyResources = [
    'Food',
    'Utilities',
    'Phone',
    'Child Care',
    'Clothing',
    'Medicine or Any Health Care (Medical, Dental, Mental Health, Vision)',
    'Other (please write in)',
] as const satisfies readonly string[];
export type FamilyResource = typeof FamilyResources[number];
export const OtherFamilyResource: FamilyResource = 'Other (please write in)';

interface OpenAnswer<tAnswer extends string, > {
    other: string,
    selected: tAnswer[],
}

export type Answer4 = OpenAnswer<FamilyResource>

export const TransportationDifficulties = [
    'Yes, it has kept me from medical appointments or from getting my medications',
    'Yes, it has kept me from non-medical meetings, appointments, work, or from getting things that I need',
    'No',
] as const satisfies readonly string[];
export type TransportationDifficulty = typeof TransportationDifficulties[number];
export const ResetDifficulty: TransportationDifficulty = 'No';

export const ToggleItem = <tField extends DirtyField | WontAnswerField>(
    array: tField[],
    item: tField,
    checked: boolean,
): tField[] => {
    const tmp = array?.length > 0 ? [...array] : [];
    const splice = (i: string) => tmp.splice(tmp.indexOf(i as tField), 1);
    const proceed = checked ? !tmp.includes(item) : tmp.includes(item);
    if (proceed) {
        checked ? tmp.push(item) : splice(item);
    }
    return tmp.sort();
};

type DirtyValidator = (form: null | undefined | VbidForm) => boolean;
const GetDirtyValidator =
    <key extends AnswerField, dirtyKey extends DirtyField>(
        field: key,
        dirtyField: dirtyKey,
        isDefaultValue: (field: VbidForm[key]) => boolean,
    ): DirtyValidator => (form) => !!form?.dirtyFields?.includes(dirtyField) && isDefaultValue(form[field]);

const isEmptyArray = (answer: string[]): boolean => answer.length === 0;
export const IsDirty1 = GetDirtyValidator('answer1', 'dirty1', (answer) => answer === '');
export const IsDirty2 = GetDirtyValidator('answer2', 'dirty2', isEmptyArray);
export const IsDirty3 = GetDirtyValidator('answer3', 'dirty3', isEmptyArray);
export const IsDirty4 = GetDirtyValidator('answer4', 'dirty4', (answer) => {
    const didntSpecify = answer.selected.includes(OtherFamilyResource) && answer.other === '';
    return isEmptyArray(answer.selected) || didntSpecify;
});
export const IsDirty5 = GetDirtyValidator('answer5', 'dirty5', isEmptyArray);

const ValidNumberResponse = (number: number | any) => typeof number === 'number' || number > 0;
const ValidStringArrayResponse = (array: string[]) => array.length > 0;
const ValidOpenAnswer = <tAnswer extends OpenAnswer<any>>({ selected, other }: tAnswer) => {
    const validOther = selected.includes(OtherFamilyResource)
        ? other.length > 0
        : true;
    return ValidStringArrayResponse(selected) && validOther;
};

type FieldValidator = (form: VbidForm) => DirtyField | true;
const GetFieldValidator =
    <key extends AnswerField>(
        field: key,
        dirtyField: DirtyField,
        wontAnswerField: WontAnswerField,
        validator: (v: VbidForm[key]) => boolean,
    ): FieldValidator =>
        (form: VbidForm) =>
            form.wontAnswer.includes(wontAnswerField) || validator(form[field]) || dirtyField;

export const GetInvalidAnswers = (form: VbidForm): Observable<DirtyField[]> => {
    const isDirtyField = (object: any): object is DirtyField => (object as DirtyField)?.length > 0;
    const formValidationResults = from([
        GetFieldValidator('answer1', 'dirty1', 'wontAnswer1', ValidNumberResponse)(form),
        GetFieldValidator('answer2', 'dirty2', 'wontAnswer2', ValidStringArrayResponse)(form),
        GetFieldValidator('answer3', 'dirty3', 'wontAnswer3', ValidStringArrayResponse)(form),
        GetFieldValidator('answer4', 'dirty4', 'wontAnswer4', ValidOpenAnswer)(form),
        GetFieldValidator('answer5', 'dirty5', 'wontAnswer5', ValidStringArrayResponse)(form),
    ]);
    return formValidationResults.pipe(filter(isDirtyField), toArray());
};
